import Vue from 'vue'

const OrderStatus = {
    CREATED: 1,
    PENDING_PAYMENT: 2,
    FAILED: 3,
    CANCELED: 4,
    PROCESSING: 5,
    COMPLETED: 6
}

const state = {
    picaServiceId: null,
    orders: [],
    currentOrder: null,
    orderItems: [],

    // Ecommerce preferences
    preferences: null,

    // Complete media data with print status for prefetch approach
    orderItemsMedia: {},
    // Structure: {
    //   [order_item_id]: {
    //     allMediaIds: [],      // All available media IDs for this item
    //     printedMediaIds: [],  // Media IDs that have been printed (deprecated)
    //     printedCopies: {},    // { [mediaId]: copiesCount } - tracks copies printed per media
    //     requiredCopies: 1,    // Number of copies required (from item.quantity)
    //     selectedIds: [],     // Currently selected media IDs for operations
    //     media: [],           // Complete array of media objects
    //     loaded: false,       // Flag if we've loaded all media
    //     loading: false,      // Loading state
    //     error: null         // Error if fetch failed
    //   }
    // }

    // All print jobs for the current order
    orderPrintJobs: [],

    // Print jobs for general spool view (last 48h)
    printJobs: [],

    // Currently selected/active print job for detail view with photos
    activePrintJob: null,

    // Cache for print job photos
    printJobPhotos: {},
    // Structure: {
    //   [print_job_id]: [...photos]
    // }

    // Selected media IDs from print jobs
    printJobSelections: {},
    // Structure: {
    //   [print_job_id]: [selectedMediaIds]
    //   }
    // }

    filters: {
        status: OrderStatus.PROCESSING, // Default: show only orders to print (PROCESSING)
        has_printable_items: true,
        order_number: '',
        created_at_range: null,
        page: 1,
        page_size: 50
    },
    loading: {
        orders: false,
        orderDetail: false,
        printJobs: false
    },
    totalOrders: 0,
    totalPages: 0
}

// Helper to normalize order data
const normalizeOrder = (order) => {
    if (!order) return null
    return {
        ...order,
        // Extract order number from payment_data (no fallback for consistency)
        order_number: (order.payment_data &&
            order.payment_data.metadata &&
            order.payment_data.metadata.sapi &&
            order.payment_data.metadata.sapi.sale_response &&
            order.payment_data.metadata.sapi.sale_response.SalNo) || '-',
        // Extract transaction number from payment_data.metadata
        transaction_number: (order.payment_data &&
            order.payment_data.metadata &&
            order.payment_data.metadata.transaction_number) || '-',
        // Extract cabin, guest, and badge IDs from payment_data.customer
        cabin_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.cabin_id) || '-',
        guest_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.guest_id) || '-',
        badge_id: (order.payment_data &&
            order.payment_data.customer &&
            order.payment_data.customer.badge_id) || '-'
    }
}

export const getters = {
    picaServiceId: state => state.picaServiceId,
    preferences: state => state.preferences,
    autoprintEnabled: state => state.preferences?.autoprint_enabled ?? false,
    orders: state => state.orders.map(normalizeOrder),
    currentOrder: state => {
        const order = normalizeOrder(state.currentOrder)
        if (!order) return null

        // Build bundles from order items
        const bundleGroups = {}
        const bundles = []

        state.orderItems.forEach(item => {
            if (item.bundle && item.bundle.id) {
                const bundleId = String(item.bundle.id)
                if (!bundleGroups[bundleId]) {
                    bundleGroups[bundleId] = {
                        ...item.bundle,
                        items: [],
                        total_price: 0,
                        discount_amount: 0,
                        unit_price: 0,
                        quantity: item.bundle_quantity
                    }
                    bundles.push(bundleGroups[bundleId])
                }
                bundleGroups[bundleId].items.push(item)
                bundleGroups[bundleId].total_price += parseFloat(item.total_price) || 0
                bundleGroups[bundleId].discount_amount += parseFloat(item.discount_amount) || 0
                bundleGroups[bundleId].unit_price += parseFloat(item.unit_price) || 0
            }
        })

        return {
            ...order,
            bundles
        }
    },
    currentOrderId: state => state.currentOrder?.id,
    orderItems: state => state.orderItems,
    orderPrintJobs: state => state.orderPrintJobs,
    getOrderItemById: state => orderItemId => {
        return state.orderItems.find(item => item.id === orderItemId)
    },

    // Active print job with enriched details
    activePrintJob: (state, getters) => {
        if (!state.activePrintJob) return null

        const orderItem = getters.getOrderItemById(state.activePrintJob.order_item_id)
        return {
            ...state.activePrintJob,
            orderItem,
            productName: orderItem?.product?.name || null,
            formatName: orderItem?.product_variant?.name || null,
            printConfig: state.activePrintJob.print_config || {}
        }
    },

    // Photos for active print job
    activePrintJobPhotos: state => {
        if (!state.activePrintJob?.id) return []
        return state.printJobPhotos[state.activePrintJob.id] || []
    },

    // Get photos for a specific print job by ID
    getPrintJobPhotos: state => printJobId => {
        return state.printJobPhotos[printJobId] || []
    },
    filters: state => state.filters,
    loading: state => state.loading,
    totalOrders: state => state.totalOrders,
    totalPages: state => state.totalPages,

    // Media getters by order item (using new prefetch structure)
    getMediaForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) {
            return { media: [], pagination: null, selectedIds: [], loading: false }
        }
        return {
            media: data.media,
            pagination: null, // Not needed with prefetch
            selectedIds: data.selectedIds || [],
            loading: data.loading
        }
    },

    // Get sorted media for order item (unprinted first)
    getSortedMediaForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        if (!data || !data.media) {
            return { media: [], pagination: null, selectedIds: [], loading: false }
        }

        const requiredCopies = data.requiredCopies || 1
        const printedCopies = data.printedCopies || {}

        // Enrich media with print info and sort: unprinted first, then by ID
        const enrichedMedia = data.media.map(media => ({
            ...media,
            print: {
                printed: printedCopies[media.id] || 0,
                total: requiredCopies
            }
        }))

        const sorted = enrichedMedia.sort((a, b) => {
            const aPrinted = a.print.printed >= a.print.total
            const bPrinted = b.print.printed >= b.print.total

            // If both have same printed status, maintain original order
            if (aPrinted === bPrinted) {
                return 0
            }

            // Unprinted items come first
            return aPrinted ? 1 : -1
        })

        return {
            media: sorted,
            pagination: null,
            selectedIds: data.selectedIds || [],
            loading: data.loading
        }
    },
    getSelectedMediaForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) return []
        return data.selectedIds || []
    },
    isMediaLoadingForOrderItem: state => orderItemId => {
        return state.orderItemsMedia[orderItemId]?.loading || false
    },

    // Get printed media IDs for order item (derived from orderPrintJobs)
    getPrintedMediaIdsForOrderItem: state => orderItemId => {
        const jobs = state.orderPrintJobs.filter(job => job.order_item_id === orderItemId)
        const printedIds = []
        jobs.forEach(job => {
            if (job.media_ids) {
                job.media_ids.forEach(id => {
                    if (!printedIds.includes(id)) {
                        printedIds.push(id)
                    }
                })
            }
        })
        return printedIds
    },

    // Get counts for printable items (using new prefetch data)
    getMediaCountsForOrderItem: state => orderItemId => {
        const prefetchData = state.orderItemsMedia[orderItemId]

        // Use new prefetch data if available
        if (prefetchData?.loaded) {
            const total = prefetchData.allMediaIds.length
            const requiredCopies = prefetchData.requiredCopies || 1

            // Count fully printed photos (all copies done)
            let fullyPrinted = 0
            let partiallyPrinted = 0
            let overPrinted = 0
            let totalCopiesPrinted = 0

            prefetchData.allMediaIds.forEach(mediaId => {
                const copiesPrinted = prefetchData.printedCopies?.[mediaId] || 0
                totalCopiesPrinted += copiesPrinted

                if (copiesPrinted > requiredCopies) {
                    overPrinted++
                    fullyPrinted++  // Also count as fully printed
                } else if (copiesPrinted === requiredCopies) {
                    fullyPrinted++
                } else if (copiesPrinted > 0) {
                    partiallyPrinted++
                }
            })

            const remaining = total - fullyPrinted

            // Fallback for old data structure
            const legacyPrinted = prefetchData.printedMediaIds?.length || 0

            return {
                total,
                printed: fullyPrinted || legacyPrinted,
                partiallyPrinted,
                overPrinted,
                remaining: remaining || (total - legacyPrinted),
                totalCopiesPrinted,
                totalCopiesRequired: total * requiredCopies,
                allLoaded: true
            }
        }

        // No legacy fallback - only use prefetch data
        return {
            total: 0,
            printed: 0,
            partiallyPrinted: 0,
            overPrinted: 0,
            remaining: 0,
            totalCopiesPrinted: 0,
            totalCopiesRequired: 0,
            allLoaded: false
        }
    },

    // New: Get total counts across all order items
    // In edgePrints all orders have printable items (has_printable_items filter is always true)
    getTotalMediaCounts: state => {
        let totalMedia = 0
        let totalPrinted = 0
        let totalRemaining = 0

        // Iterate directly on orderItems for better reactivity
        // This ensures Vue tracks dependencies correctly when media is loaded
        state.orderItems.forEach(orderItem => {
            const itemData = state.orderItemsMedia[orderItem.id]

            // Only count if media is loaded
            if (itemData?.loaded) {
                const total = itemData.allMediaIds.length
                const printed = itemData.printedMediaIds.length
                totalMedia += total
                totalPrinted += printed
                totalRemaining += (total - printed)
            }
        })

        return {
            total: totalMedia,
            printed: totalPrinted,
            remaining: totalRemaining
        }
    },

    // Check if all media is prefetched for current order
    isAllMediaPrefetched: state => {
        const printableItems = state.orderItems.filter(item => item.is_printable)
        if (printableItems.length === 0) return true

        return printableItems.every(item => {
            const itemData = state.orderItemsMedia[item.id]
            return itemData?.loaded === true
        })
    },

    // Get only unprinted media IDs for an order item
    getUnprintedMediaIdsForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]

        if (!data?.loaded) return []

        const requiredCopies = data.requiredCopies || 1

        // Return media that don't have all copies printed
        return data.allMediaIds.filter(id => {
            const copiesPrinted = data.printedCopies?.[id] || 0
            return copiesPrinted < requiredCopies
        })
    },

    // Get printed copies count for specific media
    getPrintedCopiesForMedia: state => (orderItemId, mediaId) => {
        const data = state.orderItemsMedia[orderItemId]
        return data?.printedCopies?.[mediaId] || 0
    },

    // Get required copies for an order item
    getRequiredCopiesForOrderItem: state => orderItemId => {
        const data = state.orderItemsMedia[orderItemId]
        return data?.requiredCopies || 1
    },

    // Get total count of selected media across all order items
    getTotalSelectedCount: state => {
        let total = 0
        // Count from order items
        Object.values(state.orderItemsMedia).forEach(item => {
            if (item.selectedIds) {
                total += item.selectedIds.length
            }
        })
        // Count from print jobs
        Object.values(state.printJobSelections).forEach(selectedIds => {
            total += selectedIds.length
        })
        return total
    },

    // Get selected media IDs for a print job
    getPrintJobSelectedIds: state => printJobId => {
        return state.printJobSelections[printJobId] || []
    },

    // Get all print jobs with selections
    getPrintJobsWithSelections: state => {
        return Object.keys(state.printJobSelections)
            .filter(printJobId => state.printJobSelections[printJobId].length > 0)
            .map(printJobId => {
                const printJob = [...state.orderPrintJobs, ...state.printJobs]
                    .find(job => job.id === parseInt(printJobId))
                return {
                    printJobId: parseInt(printJobId),
                    printJob,
                    selectedIds: state.printJobSelections[printJobId]
                }
            })
            .filter(item => item.printJob)
    },

    // Get all selected media with their order context
    getAllSelectedMedia: (state, getters) => {
        const selected = []
        Object.entries(state.orderItemsMedia).forEach(([orderItemId, data]) => {
            if (data.selectedIds && data.selectedIds.length > 0) {
                // Find the order item
                const orderItem = state.orderItems.find(item => item.id === parseInt(orderItemId))
                if (orderItem) {
                    data.selectedIds.forEach(mediaId => {
                        selected.push({
                            mediaId,
                            orderItemId: parseInt(orderItemId),
                            orderItem,
                            order: state.currentOrder
                        })
                    })
                }
            }
        })
        return selected
    },

    // Check if selected items include gallery-based order items (not specific media_ids)
    hasGalleryItemsSelected: (state, getters) => {
        const selectedMedia = getters.getAllSelectedMedia
        return selectedMedia.some(item => {
            const orderItem = item.orderItem
            // Gallery items are those WITHOUT specific media_ids
            // If media_ids is empty or not present, it's a gallery-based item
            return !orderItem.media_ids || orderItem.media_ids.length === 0
        })
    },

    // Print jobs for current order with enriched order item details
    orderPrintJobsEnriched: (state, getters) => {
        const currentOrderId = getters.currentOrderId
        if (!currentOrderId) return []

        // Filter print jobs for current order
        const targetId = String(currentOrderId)
        const printJobs = state.orderPrintJobs.filter(job => String(job.order_id) === targetId)

        // Enrich each print job with order item details
        return printJobs.map(job => {
            const orderItem = getters.getOrderItemById(job.order_item_id)
            return {
                ...job,
                orderItem,
                productName: orderItem?.product?.name || null,
                formatName: orderItem?.product_variant?.name || null,
                printConfig: job.print_config || {}
            }
        })
    },

    // Print jobs for general spool view (last 48h)
    printJobs: state => state.printJobs,

    // Computed getters
    printableOrderItems: state => {
        return state.orderItems.filter(item => item.is_printable)
    },

    // Group order items by bundle
    groupedOrderItems: state => {
        const grouped = []
        const bundleGroups = {}

        state.orderItems.forEach(item => {
            if (item.bundle && item.bundle.id) {
                // Group bundle items by bundle.id ONLY (not media_ids)
                const groupKey = String(item.bundle.id)

                if (!bundleGroups[groupKey]) {
                    bundleGroups[groupKey] = {
                        ...item,
                        isBundleGroup: true,
                        bundleItems: [],
                        // Aggregate price fields
                        total_price: 0,
                        discount_amount: 0,
                        unit_price: 0
                    }
                    grouped.push(bundleGroups[groupKey])
                }

                bundleGroups[groupKey].bundleItems.push(item)
                // Sum total_price from all items in the bundle
                bundleGroups[groupKey].total_price += parseFloat(item.total_price) || 0
                bundleGroups[groupKey].discount_amount += parseFloat(item.discount_amount) || 0
                bundleGroups[groupKey].unit_price += parseFloat(item.unit_price) || 0

                // Use bundle_quantity from the first item
                if (item.bundle_quantity !== undefined) {
                    bundleGroups[groupKey].quantity = item.bundle_quantity
                }
            } else {
                // Non-bundle items
                grouped.push(item)
            }
        })

        return grouped
    },

    printableGroupedOrderItems: (state, getters) => {
        return getters.groupedOrderItems.filter(item => {
            if (item.isBundleGroup) {
                // Check if any item in the bundle is printable
                return item.bundleItems.some(bundleItem => bundleItem.is_printable)
            }
            return item.is_printable
        })
    },

    isProcessingOrder: state => {
        return state.currentOrder?.status === OrderStatus.PROCESSING
    },

    isCompletedOrder: state => {
        return state.currentOrder?.status === OrderStatus.COMPLETED
    },

}

export const mutations = {
    setPicaServiceId(state, id) {
        state.picaServiceId = id
    },

    setPreferences(state, preferences) {
        state.preferences = preferences
    },

    setOrders(state, orders) {
        state.orders = orders
    },

    setCurrentOrder(state, order) {
        state.currentOrder = order
        state.orderItems = order?.order_items || []
    },

    setActivePrintJob(state, printJob) {
        state.activePrintJob = printJob
    },

    setPrintJobPhotos(state, { printJobId, photos }) {
        Vue.set(state.printJobPhotos, printJobId, photos)
    },


    // Print job mutations
    setPrintJobs(state, printJobs) {
        state.printJobs = printJobs || []
    },

    addPrintJob(state, printJob) {
        // Add to general print jobs list if not already there
        if (!state.printJobs.find(job => job.id === printJob.id)) {
            state.printJobs.unshift(printJob)
        }

        // Add to order print jobs if it's for the current order (compare as strings)
        const currentOrderId = state.currentOrder?.id
        if (currentOrderId && String(printJob.order_id) === String(currentOrderId)) {
            if (!state.orderPrintJobs.find(job => job.id === printJob.id)) {
                state.orderPrintJobs.unshift(printJob)
            }

            // Update printed copies count
            const orderItemId = printJob.order_item_id
            if (orderItemId && state.orderItemsMedia[orderItemId]) {
                const mediaData = state.orderItemsMedia[orderItemId]
                if (!mediaData.printedCopies) {
                    Vue.set(mediaData, 'printedCopies', {})
                }

                // Initialize printedMediaIds if not exists
                if (!mediaData.printedMediaIds) {
                    mediaData.printedMediaIds = []
                }

                // Increment printed copies for each media in the job
                const mediaIds = printJob.media_ids || []
                const copies = printJob.quantity || 1
                mediaIds.forEach(mediaId => {
                    // Only process media IDs that belong to this order item
                    if (mediaData.allMediaIds && mediaData.allMediaIds.includes(mediaId)) {
                        const currentCopies = mediaData.printedCopies[mediaId] || 0
                        Vue.set(mediaData.printedCopies, mediaId, currentCopies + copies)

                        // Also update printedMediaIds for backward compatibility
                        if (!mediaData.printedMediaIds.includes(mediaId)) {
                            mediaData.printedMediaIds.push(mediaId)
                        }
                    }
                })
            }
        }
    },

    setPrintJobsForOrder(state, printJobs) {
        // Store all print jobs for the order
        state.orderPrintJobs = printJobs || []

        // Reset printed copies for all order items first
        Object.keys(state.orderItemsMedia).forEach(orderItemId => {
            if (state.orderItemsMedia[orderItemId]) {
                Vue.set(state.orderItemsMedia[orderItemId], 'printedCopies', {})
                // Also reset printedMediaIds
                state.orderItemsMedia[orderItemId].printedMediaIds = []
            }
        })

        // Update printed copies count based on print jobs
        printJobs.forEach(job => {
            const orderItemId = job.order_item_id
            if (!orderItemId) return

            // Update printed copies in prefetch data if available
            if (state.orderItemsMedia[orderItemId]) {
                const itemData = state.orderItemsMedia[orderItemId]

                // Initialize printedCopies if not exists
                if (!itemData.printedCopies) {
                    Vue.set(itemData, 'printedCopies', {})
                }

                // Initialize printedMediaIds if not exists
                if (!itemData.printedMediaIds) {
                    itemData.printedMediaIds = []
                }

                // Count copies for each media ID - all jobs count as printed
                if (job.media_ids && Array.isArray(job.media_ids)) {
                    const copies = job.quantity || 1
                    job.media_ids.forEach(mediaId => {
                        // Only process media IDs that belong to this order item
                        if (itemData.allMediaIds && itemData.allMediaIds.includes(mediaId)) {
                            const currentCopies = itemData.printedCopies[mediaId] || 0
                            const newCopies = currentCopies + copies
                            Vue.set(itemData.printedCopies, mediaId, newCopies)

                            // Also update printedMediaIds for backward compatibility
                            if (!itemData.printedMediaIds.includes(mediaId)) {
                                itemData.printedMediaIds.push(mediaId)
                            }
                        }
                    })
                }
            }
        })
    },

    updatePrintJobStatus(state, { jobId, status }) {
        // Update in orderPrintJobs array
        const job = state.orderPrintJobs.find(j => j.id === jobId)
        if (job) {
            job.status = status
        }

    },


    setFilters(state, filters) {
        state.filters = { ...state.filters, ...filters }
    },

    setLoading(state, { type, value }) {
        state.loading[type] = value
    },

    setTotalOrders(state, total) {
        state.totalOrders = total
    },

    setTotalPages(state, pages) {
        state.totalPages = pages
    },

    updateOrderStatus(state, status) {
        if (state.currentOrder) {
            state.currentOrder.status = status
        }
    },

    // New mutations for prefetch approach
    setOrderItemMediaLoading(state, { orderItemId, loading }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                printedMediaIds: [],
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }
        state.orderItemsMedia[orderItemId].loading = loading
    },

    setOrderItemMediaData(state, { orderItemId, media, printedMediaIds, requiredCopies = 1 }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                printedMediaIds: [],
                printedCopies: {},
                requiredCopies: 1,
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }

        const itemData = state.orderItemsMedia[orderItemId]

        // Use Vue.set for reactivity
        Vue.set(itemData, 'media', media)
        Vue.set(itemData, 'allMediaIds', media.map(m => m.id))
        Vue.set(itemData, 'requiredCopies', requiredCopies)

        // Convert old printedMediaIds to printedCopies format
        if (printedMediaIds && printedMediaIds.length > 0) {
            const printedCopiesObj = {}
            printedMediaIds.forEach(id => {
                // Assume 1 copy printed for backward compatibility
                printedCopiesObj[id] = 1
            })
            Vue.set(itemData, 'printedCopies', printedCopiesObj)
            Vue.set(itemData, 'printedMediaIds', printedMediaIds) // Keep for backward compatibility
        } else {
            Vue.set(itemData, 'printedCopies', itemData.printedCopies || {})
        }

        Vue.set(itemData, 'loaded', true)
        Vue.set(itemData, 'loading', false)
        Vue.set(itemData, 'error', null)
    },

    setOrderItemMediaError(state, { orderItemId, error }) {
        if (!state.orderItemsMedia[orderItemId]) {
            Vue.set(state.orderItemsMedia, orderItemId, {
                allMediaIds: [],
                printedMediaIds: [],
                selectedIds: [],
                media: [],
                loaded: false,
                loading: false,
                error: null
            })
        }
        state.orderItemsMedia[orderItemId].error = error
        state.orderItemsMedia[orderItemId].loading = false
    },

    updatePrintedMediaIds(state, { orderItemId, mediaIds, copies = 1 }) {
        if (state.orderItemsMedia[orderItemId]) {
            const itemData = state.orderItemsMedia[orderItemId]

            // Initialize printedCopies if not exists
            if (!itemData.printedCopies) {
                Vue.set(itemData, 'printedCopies', {})
            }

            // Update printed copies for each media
            // NO LIMIT - allow tracking over-printing
            mediaIds.forEach(id => {
                const currentCopies = itemData.printedCopies[id] || 0
                // Use Vue.set for reactivity
                Vue.set(itemData.printedCopies, id, currentCopies + copies)
            })

            // Force reactivity by updating the entire object
            Vue.set(state.orderItemsMedia, orderItemId, { ...itemData })
        }
    },

    clearAllOrderItemData(state) {
        state.orderItemsMedia = {}
        state.orderPrintJobs = []
        state.activePrintJob = null
        state.printJobPhotos = {}
    },

    invalidateOrderItemMedia(state, { orderItemId }) {
        if (state.orderItemsMedia[orderItemId]) {
            state.orderItemsMedia[orderItemId].loaded = false
        }
    },

    setOrderItemMediaSelection(state, { orderItemId, selectedIds }) {
        if (state.orderItemsMedia[orderItemId]) {
            state.orderItemsMedia[orderItemId].selectedIds = selectedIds
        }
    },

    setPrintJobMediaSelection(state, { printJobId, selectedIds }) {
        Vue.set(state.printJobSelections, printJobId, selectedIds)
    },

    clearPrintJobSelection(state, printJobId) {
        Vue.delete(state.printJobSelections, printJobId)
    },

    clearAllPrintJobSelections(state) {
        state.printJobSelections = {}
    }
}

export const actions = service => ({
    setPicaServiceId({ commit }, id) {
        commit('setPicaServiceId', id)
    },

    async fetchPreferences({ commit, state }) {
        const preferences = await service.fetchPreferences(state.picaServiceId)
        commit('setPreferences', preferences)
        return preferences
    },

    async updateAutoprintEnabled({ commit, state }, enabled) {
        const preferences = await service.updatePreferences(state.picaServiceId, {
            autoprint_enabled: enabled
        })
        commit('setPreferences', preferences)
        return preferences
    },

    async setActivePrintJob({ commit, dispatch, state }, printJob) {
        commit('setActivePrintJob', printJob)

        // If print job has media, fetch photos if not already cached
        if (printJob?.id && printJob?.media_ids?.length > 0) {
            const cached = state.printJobPhotos[printJob.id]

            // Skip if already fetched
            if (cached && cached.length > 0) {
                return
            }

            // Fetch photos for this print job
            await dispatch('fetchPrintJobPhotos', printJob)
        }
    },

    async fetchPrintJobPhotos({ commit, state }, printJob) {
        if (!printJob?.id || !printJob?.media_ids?.length) return

        const printJobId = printJob.id

        // Get pica_service_id from print job itself, or fallback to state
        const picaServiceId = printJob.pica_service_id || state.picaServiceId

        if (!picaServiceId) {
            console.error('PicaServiceId not found - cannot fetch print job photos')
            // Commit empty array so the component knows there are no photos
            commit('setPrintJobPhotos', {
                printJobId,
                photos: []
            })
            return []
        }

        // Fetch media with picacode and media_ids
        const response = await service.fetchEdgeOrderMedia(
            picaServiceId,
            {
                picacode: printJob.picacode,
                media_ids: printJob.media_ids
            },
            { page: 1, page_size: 100 }
        )

        // Store photos in cache
        commit('setPrintJobPhotos', {
            printJobId,
            photos: response.results || []
        })

        return response.results
    },

    async fetchOrders({ commit, state }) {
        commit('setLoading', { type: 'orders', value: true })
        try {
            const params = {
                ...state.filters,
                page: state.filters.page,
                page_size: state.filters.page_size
            }

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === '') {
                    delete params[key]
                }
            })

            const response = await service.fetchOrders(state.picaServiceId, params)
            commit('setOrders', response.results || response)

            // Use pagination data from headers if available
            if (response.pagination) {
                commit('setTotalOrders', response.pagination.total || 0)
                commit('setTotalPages', response.pagination.num_pages || 1)
            } else {
                // Fallback to old calculation
                commit('setTotalOrders', response.count || 0)
                commit('setTotalPages', Math.ceil((response.count || 0) / state.filters.page_size))
            }

            return response
        } catch (error) {
            throw error
        } finally {
            commit('setLoading', { type: 'orders', value: false })
        }
    },

    async fetchOrderDetail({ commit }, orderId) {
        commit('setLoading', { type: 'orderDetail', value: true })
        try {
            const order = await service.fetchOrderDetail(orderId)
            commit('setCurrentOrder', order)
            return order
        } catch (error) {
            throw error
        } finally {
            commit('setLoading', { type: 'orderDetail', value: false })
        }
    },

    async fetchPrintJobsForOrder({ commit, getters, state }, orderIdParam = null) {
        // Use provided orderId or fallback to current order
        const orderId = orderIdParam || getters.currentOrderId || state.currentOrder?.id

        if (!orderId) {
            return []
        }

        commit('setLoading', { type: 'printJobs', value: true })
        try {
            // API returns array directly: [{...}, {...}]
            // page_size: 100 per vedere fino a 100 print jobs per ordine
            const printJobs = await service.fetchPrintJobs({
                order_id: orderId,
                page_size: 100
            })

            commit('setPrintJobsForOrder', printJobs || [])
            return printJobs
        } catch (error) {
            throw error
        } finally {
            commit('setLoading', { type: 'printJobs', value: false })
        }
    },

    async fetchPrintJobs({ commit }, params = {}) {
        // API returns array directly: [{...}, {...}]
        // Params può includere: status (1=PENDING, 3=COMPLETED)
        const printJobs = await service.fetchPrintJobs(params)
        commit('setPrintJobs', printJobs || [])
        return printJobs
    },

    // New: Prefetch all media for all order items (both printable and digital)
    async prefetchAllOrderMedia({ commit, state, dispatch }) {
        // Get all items that have media (printable or digital)
        const itemsWithMedia = state.orderItems.filter(item =>
            item.is_printable ||
            (item.media_ids && item.media_ids.length > 0) ||
            (item.gallery_ids && item.gallery_ids.length > 0)
        )

        if (itemsWithMedia.length === 0) {
            return { success: true, itemsLoaded: 0 }
        }

        // First, ensure we have print jobs loaded to know what's printed
        const printJobs = await dispatch('fetchPrintJobsForOrder')

        // Load all media for each item with media in parallel
        const promises = itemsWithMedia.map(async (orderItem) => {
            const orderItemId = orderItem.id

            // Skip if already loaded
            if (state.orderItemsMedia[orderItemId]?.loaded) {
                return { orderItemId, success: true, cached: true }
            }

            commit('setOrderItemMediaLoading', { orderItemId, loading: true })

            try {
                const picaCode = orderItem.picacode || state.currentOrder?.picacode
                if (!picaCode) {
                    window.log('Order item without picacode - skipping:', {
                        orderItemId,
                        orderItem,
                        currentOrderPicacode: state.currentOrder?.picacode
                    })
                    return { orderItemId, success: true, skipped: true, reason: 'No picacode' }
                }

                // Build request params
                const params = { picacode: picaCode }

                // Add media_ids or gallery_ids if available
                if (orderItem.media_ids?.length > 0) {
                    params.media_ids = orderItem.media_ids
                } else if (orderItem.gallery_ids?.length > 0) {
                    params.gallery_ids = orderItem.gallery_ids
                } else if (orderItem.product?.gallery_ids?.length > 0) {
                    params.gallery_ids = orderItem.product.gallery_ids
                }

                // Fetch all pages
                let allMedia = []
                let page = 1
                let hasMore = true

                while (hasMore) {
                    const response = await service.fetchEdgeOrderMedia(
                        state.picaServiceId,
                        params,
                        {
                            page,
                            page_size: 100
                        }
                    )

                    allMedia = [...allMedia, ...(response.results || [])]

                    // Check if there are more pages
                    hasMore = response.pagination?.next || false
                    page++

                    // Safety check
                    if (page > 100) {
                        console.warn('Stopping after 100 pages')
                        break
                    }
                }

                // Filter only printable media if the order item is printable
                // Some media might be digital even in printable items
                const filteredMedia = orderItem.is_printable
                    ? allMedia.filter(m => m.is_printable !== false)
                    : allMedia

                // Store the complete data (without printedMediaIds - will be calculated after)
                commit('setOrderItemMediaData', {
                    orderItemId,
                    media: filteredMedia,
                    printedMediaIds: [], // Empty for now
                    requiredCopies: orderItem.quantity || 1
                })

                return { orderItemId, success: true, mediaCount: allMedia.length }

            } catch (error) {
                console.error(`Failed to prefetch media for order item ${orderItemId}:`, error)
                commit('setOrderItemMediaError', { orderItemId, error: error.message })
                return { orderItemId, success: false, error: error.message }
            }
        })

        const results = await Promise.all(promises)

        // Now that all media is loaded with allMediaIds populated,
        // recalculate printed copies from print jobs
        commit('setPrintJobsForOrder', printJobs)

        const successCount = results.filter(r => r.success).length
        const failureCount = results.filter(r => !r.success).length

        return {
            success: failureCount === 0,
            itemsLoaded: successCount,
            itemsFailed: failureCount,
            results
        }
    },

    // Fetch printable media by IDs - used in print spool context
    // where media is implicitly printable (will always request with watermark)
    async fetchPrintableMediaByIds({ state }, { picaServiceId, picaCode, mediaIds }) {
        try {
            const params = {
                picacode: picaCode
            }

            // Add media_ids only if provided
            if (mediaIds && mediaIds.length > 0) {
                params.media_ids = mediaIds
            }

            // Fetch media for print spool context
            const response = await service.fetchEdgeOrderMedia(
                picaServiceId || state.picaServiceId,
                params,
                {
                    page: 1,
                    page_size: 100
                }
            )

            return response
        } catch (error) {
            throw error
        }
    },


    async createReprintJob({ commit, state }, { originalPrintJob, mediaIds, orderId }) {
        try {
            // Chunk media IDs into groups of max 500
            const CHUNK_SIZE = 500
            const chunks = []
            for (let i = 0; i < mediaIds.length; i += CHUNK_SIZE) {
                chunks.push(mediaIds.slice(i, i + CHUNK_SIZE))
            }

            // Build items array for bulk API call
            const items = chunks.map(mediaChunk => ({
                order_number: originalPrintJob.order_number,
                order_id: orderId,
                order_item_id: originalPrintJob.order_item_id,
                user_id: originalPrintJob.user_id,
                pica_service_id: originalPrintJob.pica_service_id || state.picaServiceId,
                media_ids: mediaChunk,
                print_config: originalPrintJob.print_config || {},
                is_reprint: true,
                original_job_id: originalPrintJob.id
            }))

            // Single API call with items array
            const results = await service.createPrintJobs(items)

            // Add all created print jobs to the store
            if (Array.isArray(results)) {
                results.forEach(printJob => {
                    commit('addPrintJob', printJob)
                })
            }

            return results
        } catch (error) {
            throw error
        }
    },

    async downloadMedia({ state, dispatch }, { mediaIds, picaCode, picaServiceId, printable }) {
        const effectivePicaServiceId = picaServiceId || state.picaServiceId
        const count = mediaIds ? mediaIds.length : 0

        // Helper to download files from response
        const downloadFiles = async (response) => {
            if (response.files && Array.isArray(response.files)) {
                const { default: downloader } = await import('@/domain/core/utils/downloader')
                for (const file of response.files) {
                    downloader.downloadFromUrl(file.url, file.filename)
                }
                return response.files.length
            }
            return 0
        }

        return await dispatch('downloader/download', {
            name: `Print ${picaCode || 'Download'}`,
            count,
            // Direct download - no pagination
            onDirect: async () => {
                const response = await service.downloadMedia(effectivePicaServiceId, {
                    picacode: picaCode,
                    media_ids: mediaIds,
                    printable
                })
                await downloadFiles(response)
            },
            // Batch download - backend handles pagination
            onBatch: async (page, limit) => {
                const response = await service.downloadMedia(effectivePicaServiceId, {
                    picacode: picaCode,
                    media_ids: mediaIds,
                    printable,
                    page,
                    limit
                })
                return await downloadFiles(response)
            }
            // No onEmail - edgePrints doesn't support email
        }, { root: true })
    },

    async getAllOrderMediaIds({ state }) {
        // Collect all media IDs from all order items
        const allMediaIds = []

        for (const orderItemId in state.orderItemsMedia) {
            const mediaData = state.orderItemsMedia[orderItemId]
            if (mediaData?.loaded && mediaData.allMediaIds) {
                allMediaIds.push(...mediaData.allMediaIds)
            }
        }

        return allMediaIds
    },

    // Create print job for selected media in an order item
    async createPrintJobForOrderItem({ dispatch, state }, { orderItem, order, copies = 1 }) {
        // Get selected media IDs for this order item
        const selectedIds = state.orderItemsMedia[orderItem.id]?.selectedIds || []

        if (selectedIds.length === 0) {
            throw new Error('No media selected for printing')
        }

        // Always use array format, even for single item
        return dispatch('createPrintJobs', [{
            orderItem,
            order,
            mediaIds: selectedIds,
            copies
        }])
    },

    /**
     * Create print jobs (always accepts an array, even for single item)
     * @param {Array} orderItemsData - Array of {orderItem, order, mediaIds, copies, isPrintJobReprint?, originalPrintJob?}
     * @returns {Array} Created print jobs
     */
    async createPrintJobs({ commit, state }, orderItemsData) {
        if (!orderItemsData || orderItemsData.length === 0) {
            throw new Error('No order items provided')
        }

        const CHUNK_SIZE = 500

        // Build items array for bulk API call
        const items = []

        for (const itemData of orderItemsData) {
            const { orderItem, order, mediaIds, copies = 1, isPrintJobReprint = false, originalPrintJob = null } = itemData

            if (!mediaIds || mediaIds.length === 0) {
                continue // Skip items without media
            }

            // Chunk media IDs into groups of max 500
            const chunks = []
            for (let i = 0; i < mediaIds.length; i += CHUNK_SIZE) {
                chunks.push(mediaIds.slice(i, i + CHUNK_SIZE))
            }

            // Create an item entry for each chunk
            for (const mediaChunk of chunks) {
                let printJobData

                if (isPrintJobReprint && originalPrintJob) {
                    // For print job reprints, use the original print job data
                    printJobData = {
                        order_number: originalPrintJob.order_number,
                        order_id: order.id || originalPrintJob.order_id,
                        order_item_id: originalPrintJob.order_item_id,
                        user_id: originalPrintJob.user || order.user_id,
                        pica_service_id: originalPrintJob.pica_service_id || state.picaServiceId,
                        media_ids: mediaChunk,
                        print_config: originalPrintJob.print_config || {},
                        quantity: copies,
                        is_reprint: true,
                        original_job_id: originalPrintJob.id
                    }
                } else {
                    // For normal prints from order items
                    printJobData = {
                        order_number: order.order_number,
                        order_id: order.id,
                        order_item_id: orderItem.id,
                        user_id: order.user_id,
                        pica_service_id: orderItem.pica_service_id || state.picaServiceId,
                        media_ids: mediaChunk,
                        print_config: orderItem.product_variant?.config || orderItem.product?.config || {},
                        quantity: copies
                    }
                }

                items.push(printJobData)
            }
        }

        if (items.length === 0) {
            throw new Error('No valid items to print')
        }

        try {
            // Single API call with all items
            const results = await service.createPrintJobs(items)

            // Add all created print jobs to the store
            if (Array.isArray(results)) {
                results.forEach(printJob => {
                    commit('addPrintJob', printJob)
                })
            }

            return results
        } catch (error) {
            throw error
        }
    },

    updatePrintJobStatus({ commit }, payload) {
        commit('updatePrintJobStatus', payload)
    },

    // Print all missing media across all order items
    async printAllMedia({ state, dispatch }) {
        // Print ALL media for all printable items, regardless of print status
        const printableItems = state.orderItems.filter(item => item.is_printable)

        // Prepare all order items data for bulk print
        const orderItemsData = []
        let totalPrinted = 0

        for (const orderItem of printableItems) {
            const mediaData = state.orderItemsMedia[orderItem.id]

            // Skip if not loaded
            if (!mediaData?.loaded) continue
            if (mediaData.allMediaIds.length === 0) continue

            orderItemsData.push({
                orderItem,
                order: state.currentOrder,
                mediaIds: mediaData.allMediaIds
            })

            totalPrinted += mediaData.allMediaIds.length
        }

        if (orderItemsData.length === 0) {
            throw new Error('No media to print')
        }

        try {
            // Single bulk API call for all order items
            await dispatch('createPrintJobs', orderItemsData)

            return {
                totalPrinted,
                errors: [],
                success: true
            }
        } catch (error) {
            console.error('Failed to print all media:', error)
            throw new Error('Failed to create print jobs')
        }
    },

    async printAllMissingMedia({ state, dispatch }) {
        const printableItems = state.orderItems.filter(item => item.is_printable)

        // Prepare all order items data for bulk print
        const orderItemsData = []
        let totalPrinted = 0

        for (const orderItem of printableItems) {
            const mediaData = state.orderItemsMedia[orderItem.id]

            // Skip if not loaded or no missing media
            if (!mediaData?.loaded) continue

            const unprintedIds = mediaData.allMediaIds.filter(id =>
                !mediaData.printedMediaIds.includes(id)
            )

            if (unprintedIds.length === 0) continue

            orderItemsData.push({
                orderItem,
                order: state.currentOrder,
                mediaIds: unprintedIds
            })

            totalPrinted += unprintedIds.length
        }

        if (orderItemsData.length === 0) {
            throw new Error('No missing media to print')
        }

        try {
            // Single bulk API call for all order items
            await dispatch('createPrintJobs', orderItemsData)

            return {
                totalPrinted,
                errors: [],
                success: true
            }
        } catch (error) {
            console.error('Failed to print missing media:', error)
            throw new Error('Failed to create print jobs')
        }
    },


    async updateOrderStatus({ commit }, { orderId, status }) {
        try {
            const result = await service.updateOrderStatus(orderId, status)
            commit('updateOrderStatus', status)
            return result
        } catch (error) {
            throw error
        }
    },

    updateFilters({ commit }, filters) {
        commit('setFilters', filters)
    },

    clearCurrentOrder({ commit }) {
        commit('setCurrentOrder', null)
        commit('clearAllOrderItemData')
    },

    // Media selection action
    toggleMediaSelectionForOrderItem({ state, commit }, { orderItemId, mediaId }) {
        const data = state.orderItemsMedia[orderItemId]
        if (!data) return

        const selectedIds = data.selectedIds || []
        const index = selectedIds.indexOf(mediaId)

        if (index > -1) {
            // Deselect
            selectedIds.splice(index, 1)
        } else {
            // Select
            selectedIds.push(mediaId)
        }

        commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [...selectedIds] })
    },

    selectAllMediaForOrderItem({ state, commit }, orderItemId) {
        const data = state.orderItemsMedia[orderItemId]
        if (!data || !data.allMediaIds) return

        commit('setOrderItemMediaSelection', {
            orderItemId,
            selectedIds: [...data.allMediaIds]
        })
    },

    deselectAllMediaForOrderItem({ commit }, orderItemId) {
        commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [] })
    },

    // Clear all selections across all order items and print jobs
    clearAllSelections({ state, commit }) {
        // Clear order item selections
        Object.keys(state.orderItemsMedia).forEach(orderItemId => {
            if (state.orderItemsMedia[orderItemId]?.selectedIds?.length > 0) {
                commit('setOrderItemMediaSelection', { orderItemId, selectedIds: [] })
            }
        })
        // Clear print job selections
        commit('clearAllPrintJobSelections')
    },

    async selectAllMediaWithFetch({ state, commit, dispatch }, { orderItemId, orderItem, picaCode }) {
        // Ensure all media is loaded
        const data = state.orderItemsMedia[orderItemId]

        if (!data || !data.loaded) {
            // Load all media first
            await dispatch('prefetchOrderItemMedia', { orderItem, picaCode })
        }

        // Select all media IDs
        const updatedData = state.orderItemsMedia[orderItemId]
        if (updatedData && updatedData.allMediaIds) {
            commit('setOrderItemMediaSelection', {
                orderItemId,
                selectedIds: [...updatedData.allMediaIds]
            })
        }
    },

    // Compatibility action for components still using lazy load
    // Uses prefetched data when available
    async fetchMediaForOrderItemId({ state, dispatch, commit }, { orderItemId, orderItem, picaCode, page = 1, perPage = 100 }) {
        // Check if we have prefetched data
        const prefetchData = state.orderItemsMedia[orderItemId]

        if (prefetchData?.loaded) {
            // Return prefetched data in the old format
            return {
                results: prefetchData.media,
                pagination: {
                    page: 1,
                    total: prefetchData.media.length,
                    next: null,
                    previous: null
                }
            }
        }

        // If no prefetch data, fetch it directly from the API
        if (orderItem && picaCode) {
            try {
                // Build request params with media_ids or gallery_ids
                const requestParams = { picacode: picaCode }

                // Add media_ids or gallery_ids if available
                if (orderItem.media_ids && orderItem.media_ids.length > 0) {
                    requestParams.media_ids = orderItem.media_ids
                } else if (orderItem.gallery_ids && orderItem.gallery_ids.length > 0) {
                    requestParams.gallery_ids = orderItem.gallery_ids
                }

                const response = await service.fetchEdgeOrderMedia(
                    state.picaServiceId,
                    requestParams,
                    {
                        page,
                        page_size: perPage
                    }
                )

                // Store in cache for future use
                if (response.results) {
                    commit('setOrderItemMediaData', {
                        orderItemId,
                        media: response.results,
                        printedMediaIds: []
                    })
                }

                return response
            } catch (error) {
                console.error('Failed to fetch media:', error)
            }
        }

        // Return empty if nothing worked
        return {
            results: [],
            pagination: null
        }
    },

    // Hide/soft delete media (only for gallery-based items)
    async hideSelectedMedia({ state, commit, dispatch, getters }) {
        const selectedMedia = getters.getAllSelectedMedia

        // Filter only gallery-based items (items without specific media_ids)
        const galleryItems = selectedMedia.filter(item => {
            const orderItem = item.orderItem
            // If media_ids is empty or not present, it's a gallery-based item
            return !orderItem.media_ids || orderItem.media_ids.length === 0
        })

        if (galleryItems.length === 0) {
            throw new Error('No gallery-based items selected')
        }

        // Get all media IDs
        const mediaIds = galleryItems.map(item => item.mediaId)

        // Call soft delete
        const response = await service.softDeleteMedia(state.picaServiceId, mediaIds)

        // Refresh the entire order once
        if (state.currentOrder?.id) {
            await dispatch('fetchOrderDetail', state.currentOrder.id)
        }

        return response
    },

    // Unlink media (remove picacode from photos)
    async unlinkSelectedMedia({ state, commit, dispatch, getters }) {
        const selectedMedia = getters.getAllSelectedMedia

        // Filter only gallery-based items (items without specific media_ids)
        const galleryItems = selectedMedia.filter(item => {
            const orderItem = item.orderItem
            // If media_ids is empty or not present, it's a gallery-based item
            return !orderItem.media_ids || orderItem.media_ids.length === 0
        })

        if (galleryItems.length === 0) {
            throw new Error('No gallery-based items selected')
        }

        // Get picacode from current order
        const picacode = state.currentOrder?.picacode
        if (!picacode) {
            throw new Error('No picacode found for current order')
        }

        // Get all media IDs
        const mediaIds = galleryItems.map(item => item.mediaId)

        // Call unlink with picacode
        const response = await service.unlinkPicaCode(state.picaServiceId, mediaIds, picacode)

        // Refresh the entire order once
        if (state.currentOrder?.id) {
            await dispatch('fetchOrderDetail', state.currentOrder.id)
        }

        return response
    }
})

export default edgeOrdersService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(edgeOrdersService)
})